'use client';

import type { GalleryImageViewRequest } from './editorjs-gallery-tool';

import {
  Box,
  DialogBody,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Image,
  Text,
} from '@chakra-ui/react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';

type GalleryImageModalContextValue = {
  openGalleryImage: (request: GalleryImageViewRequest) => void;
};

const GalleryImageModalContext =
  createContext<GalleryImageModalContextValue | null>(null);

export function useGalleryImageModal(): GalleryImageModalContextValue['openGalleryImage'] {
  const ctx = useContext(GalleryImageModalContext);
  return ctx?.openGalleryImage ?? (() => {});
}

export function GalleryImageModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [view, setView] = useState<GalleryImageViewRequest | null>(null);

  const openGalleryImage = useCallback((request: GalleryImageViewRequest) => {
    setView(request);
  }, []);

  const value = useMemo(() => ({ openGalleryImage }), [openGalleryImage]);

  const handleOpenChange = useCallback((e: { open: boolean }) => {
    if (!e.open) setView(null);
  }, []);

  return (
    <GalleryImageModalContext.Provider value={value}>
      {children}
      <DialogRoot
        open={Boolean(view)}
        onOpenChange={handleOpenChange}
        size="xl"
      >
        <CenteredDialogContent maxW="min(92vw, 56rem)">
          <DialogHeader>
            <DialogTitle>Gallery image</DialogTitle>
          </DialogHeader>
          <DialogBody pb={6}>
            {view ? (
              <Box
                display="flex"
                flexDirection="column"
                gap={3}
                alignItems="center"
              >
                <Image
                  src={view.url}
                  alt={view.caption ?? ''}
                  maxH="70vh"
                  w="auto"
                  maxW="100%"
                  objectFit="contain"
                  borderRadius="md"
                />
                {view.caption ? (
                  <Text
                    as="p"
                    textAlign="center"
                    w="100%"
                    color="fg.muted"
                    style={{ fontSize: 'var(--font-base-size)' }}
                  >
                    {view.caption}
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </DialogBody>
        </CenteredDialogContent>
      </DialogRoot>
    </GalleryImageModalContext.Provider>
  );
}
